import { VirtualRTStateType } from '../telemetry';
import { RTMonitoringType } from './real-time-telemetry.service';

/**
 * Normalise a raw RT timestamp coming from the backend.
 *
 * Backend may return seconds or milliseconds — if the value is below the
 * millisecond epoch threshold (1 000 000 000 000) we assume seconds and
 * convert.  Falsy/missing values are returned as `null`.
 */
export function normalizeRTTimestamp(startTS: number | undefined | null): number | null {
  if (!startTS) {
    return null;
  }
  return startTS < 1_000_000_000_000 ? startTS * 1000 : startTS;
}

/**
 * Build a clean, canonical `VirtualRTStateType` from a raw backend payload.
 *
 * - `active` is coerced to boolean
 * - `startTS` is normalised via {@link normalizeRTTimestamp}
 * - `duration` is validated with `Number.isFinite` (strict) to avoid NaN/Infinity
 * - `target` falls back to `fallbackTarget` (typically the systemId)
 */
export function normalizeVirtualRTStatus(
  status: VirtualRTStateType,
  fallbackTarget: string,
): VirtualRTStateType {
  const parsedDuration = Number(status.duration);
  return {
    active: Boolean(status.active),
    startTS: normalizeRTTimestamp(Number(status.startTS || 0)) ?? 0,
    duration: Number.isFinite(parsedDuration) ? parsedDuration : 0,
    target: status.target || fallbackTarget,
  };
}

/**
 * Compute how many milliseconds remain in an active RT session.
 *
 * Returns a negative value when the session has already expired — the caller
 * should check `> 0` before proceeding.
 */
export function getRemainingMs(status: VirtualRTStateType): number {
  return Number(status.startTS) + Number(status.duration || 0) * 1000 - Date.now();
}

/**
 * Resolve the RT monitoring type and EMS ID from a system-like object.
 *
 * Handles both shapes used across the codebase:
 *   - Objects with `monitoringType` (from `SystemDefinition` in effects)
 *   - Objects with `configuration.azzurroHubId`/`connextId` (from `System` in components)
 */
export function resolveRTMonitoringInfo(
  systemLike: {
    monitoringType?: string;
    configuration?: { azzurroHubId?: string; connextId?: string };
  } | null | undefined,
): { monitoringType: RTMonitoringType; emsId: string } | null {
  if (!systemLike) {
    return null;
  }

  // Prefer explicit monitoringType when available (SystemDefinition from effects)
  if (systemLike.monitoringType === 'AzzurroHub') {
    const emsId = systemLike.configuration?.azzurroHubId;
    return emsId ? { monitoringType: 'AzzurroHub', emsId } : null;
  }

  if (systemLike.monitoringType === 'Connext') {
    const emsId = systemLike.configuration?.connextId;
    return emsId ? { monitoringType: 'Connext', emsId } : null;
  }

  // Fallback: infer from configuration keys (System from components)
  if (systemLike.configuration?.azzurroHubId) {
    return { monitoringType: 'AzzurroHub', emsId: systemLike.configuration.azzurroHubId };
  }

  if (systemLike.configuration?.connextId) {
    return { monitoringType: 'Connext', emsId: systemLike.configuration.connextId };
  }

  return null;
}
